/**
 * VAT display toggle — storefront display-only price adjustment.
 * SSR uses tag-based defaults only (B2B → exc, else inc) for CDN-safe caching.
 * Saved preference (7 days) is read/written here via localStorage only.
 */
(function () {
  const storageKey = 'vat_display_preference';
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const shopifyPriceSelector = '[data-vat-shopify][data-vat-cents]';

  function getConfig() {
    const theme = window.theme || {};
    const vat = theme.settings?.vat || {};
    const customer = theme.customer || {};
    return {
      enabled: Boolean(vat.enabled),
      rate: parseFloat(vat.rate) || 20,
      pricesStoredAs: vat.pricesStoredAs || 'exclusive',
      isB2b: Boolean(customer.isB2b),
      moneyFormat: theme.settings?.money_with_currency_format || theme.settings?.money_format,
      currencyCodeEnabled: Boolean(theme.settings?.currency_code_enabled),
    };
  }

  function defaultMode() {
    return getConfig().isB2b ? 'exc' : 'inc';
  }

  function readStoredMode() {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.mode || !data.expiresAt) return null;
      if (Date.now() > data.expiresAt) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return data.mode === 'exc' ? 'exc' : 'inc';
    } catch (error) {
      return null;
    }
  }

  function writeStoredMode(mode) {
    try {
      const next = mode === 'exc' ? 'exc' : 'inc';
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          mode: next,
          expiresAt: Date.now() + sevenDaysMs,
        })
      );
    } catch (error) {
      /* ignore */
    }
  }

  let currentMode = null;

  function getMode() {
    if (currentMode) return currentMode;
    const config = getConfig();
    if (!config.enabled) return defaultMode();
    currentMode = readStoredMode() || defaultMode();
    return currentMode;
  }

  function setDatasetMode(mode) {
    document.documentElement.dataset.vatMode = mode;
    document.documentElement.dataset.vatEnabled = getConfig().enabled ? 'true' : 'false';
  }

  function adjustCents(cents) {
    const config = getConfig();
    const base = parseInt(cents, 10);
    if (isNaN(base) || !config.enabled) return base;

    const mode = getMode();
    const rate = config.rate;
    const multiplier = 100 + rate;

    if (config.pricesStoredAs === 'exclusive') {
      return mode === 'inc' ? Math.round((base * multiplier) / 100) : base;
    }
    return mode === 'exc' ? Math.round((base * 100) / multiplier) : base;
  }

  function formatCents(cents) {
    if (typeof formatMoney !== 'function') return String(cents);
    const config = getConfig();
    const format = config.moneyFormat;
    return formatMoney(adjustCents(cents), format);
  }

  function updatePriceElement(element) {
    const base = element.getAttribute('data-vat-cents');
    if (!base) return;

    const format = element.getAttribute('data-vat-format');
    const baseMax = element.getAttribute('data-vat-cents-max');

    if (format === 'range' && baseMax) {
      element.textContent = formatCents(base) + ' - ' + formatCents(baseMax);
      return;
    }

    const prefix = element.getAttribute('data-vat-prefix') || '';
    element.textContent = prefix + formatCents(base);
  }

  function getPriceTagLabel(mode) {
    const vat = window.theme?.settings?.vat || {};
    return mode === 'inc'
      ? vat.labelInc || 'inc. VAT'
      : vat.labelExc || 'ex. VAT';
  }

  function updateAllPriceTags() {
    if (!getConfig().enabled) return;
    const mode = getMode();
    const label = getPriceTagLabel(mode);

    document.querySelectorAll('[data-vat-price-tag]').forEach((element) => {
      element.textContent = label;
      element.setAttribute('data-vat-price-tag-mode', mode);
      element.classList.remove('vat-price-tag--inc', 'vat-price-tag--exc');
      element.classList.add(mode === 'inc' ? 'vat-price-tag--inc' : 'vat-price-tag--exc');
    });
  }

  function updateAllPrices(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const matchedRoot =
      root && root.matches && root.matches(shopifyPriceSelector) ? [root] : [];
    const nodes = matchedRoot.length
      ? matchedRoot.filter((element) => element.hasAttribute('data-vat-shopify'))
      : scope.querySelectorAll(shopifyPriceSelector);

    nodes.forEach(updatePriceElement);
    updateAllPriceTags();
  }

  function setMode(mode, options = {}) {
    const next = mode === 'exc' ? 'exc' : 'inc';
    currentMode = next;
    if (getConfig().enabled && !options.skipStorage) {
      writeStoredMode(next);
    }
    setDatasetMode(next);
    updateAllPrices();
    syncToggleUI();
    document.dispatchEvent(new CustomEvent('vat:change', { detail: { mode: next } }));
  }

  function syncToggleUI() {
    const toggle = document.querySelector('[data-vat-toggle]');
    if (!toggle) return;
    const mode = getMode();
    const isInc = mode === 'inc';
    toggle.setAttribute('aria-checked', isInc ? 'true' : 'false');
    toggle.classList.toggle('vat-toggle--inc', isInc);
    toggle.classList.toggle('vat-toggle--exc', !isInc);
  }

  function bindToggle() {
    const toggle = document.querySelector('[data-vat-toggle]');
    if (!toggle || toggle.dataset.vatBound) return;
    toggle.dataset.vatBound = 'true';

    toggle.addEventListener('click', () => {
      setMode(getMode() === 'inc' ? 'exc' : 'inc');
    });

    toggle.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setMode(getMode() === 'inc' ? 'exc' : 'inc');
      }
    });
  }

  function initEarlyMode() {
    const config = getConfig();
    if (!config.enabled) {
      document.documentElement.dataset.vatEnabled = 'false';
      return;
    }
    currentMode = readStoredMode() || defaultMode();
    setDatasetMode(currentMode);
  }

  function init() {
    initEarlyMode();
    if (!getConfig().enabled) return;

    bindToggle();
    syncToggleUI();
    updateAllPrices();

    document.addEventListener('product:variant-change', () => {
      requestAnimationFrame(() => updateAllPrices());
    });

    document.addEventListener('cart:refresh', () => {
      requestAnimationFrame(() => updateAllPrices());
    });

    document.addEventListener('line-item:change:end', () => {
      requestAnimationFrame(() => updateAllPrices());
    });

    document.addEventListener('cart:item-added', () => {
      requestAnimationFrame(() => updateAllPrices());
    });

    document.addEventListener('quick-view:open', () => {
      requestAnimationFrame(() => updateAllPrices());
    });
  }

  window.vatDisplay = {
    getMode,
    setMode,
    adjustCents,
    formatCents,
    updateAllPrices,
    readStoredMode,
    defaultMode,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
